import { diasUteis } from "./calculos";
import { hasValue } from "./data";
import { F } from "./gerencialFields";
import { compareDatesAsc, toDate } from "./status";

export function getMarcenariaDeadline(row) {
  return row[F.FINAL_MARCENARIA_REPROGRAMADO] ||
    row[F.FINAL_MARCENARIA] ||
    row[F.DATA_REPROGRAMADA] ||
    row[F.ENTREGA_PREVISTA] ||
    null;
}

export function isMarcenariaReady(row) {
  return [
    F.DATA_DE_CORTE,
    F.DATA_DE_BORDO,
    F.DATA_DE_FURACAO,
    F.DATA_DE_USINAGEM,
  ].every((field) => hasValue(row[field]));
}

export function isMarcenariaPending(row) {
  return isMarcenariaReady(row) && !hasValue(row[F.MARCENARIA_TERMINO]);
}

function maxDate(left, right) {
  if (!left) return right;
  if (!right) return left;
  return compareDatesAsc(left, right) >= 0 ? left : right;
}

function minDate(left, right) {
  if (!left) return right;
  if (!right) return left;
  return compareDatesAsc(left, right) <= 0 ? left : right;
}

export function getDiasBloqueio(
  row,
  { periodStart = null, periodEnd = null, referenceDate = new Date() } = {}
) {
  const inicioBloqueio = toDate(row?.[F.INICIO_DO_BLOQUEIO]);

  if (!inicioBloqueio) {
    return null;
  }

  const finalBloqueio =
    toDate(row?.[F.FINAL_DO_BLOQUEIO]) ||
    toDate(periodEnd) ||
    toDate(referenceDate);

  if (!finalBloqueio || compareDatesAsc(inicioBloqueio, finalBloqueio) > 0) {
    return null;
  }

  const inicioPeriodo = toDate(periodStart);
  const finalPeriodo = toDate(periodEnd);
  const inicioEfetivo = maxDate(inicioBloqueio, inicioPeriodo);
  const finalEfetivo = minDate(finalBloqueio, finalPeriodo);

  if (!inicioEfetivo || !finalEfetivo || compareDatesAsc(inicioEfetivo, finalEfetivo) > 0) {
    return null;
  }

  const dias = diasUteis(inicioEfetivo, finalEfetivo);
  return Number.isFinite(dias) && dias >= 0 ? dias : null;
}

export function getLeadTimeProducao(row, startValue, endValue) {
  const startDate = toDate(startValue);
  const endDate = toDate(endValue);

  if (!startDate || !endDate || compareDatesAsc(startDate, endDate) > 0) {
    return null;
  }

  const totalDays = diasUteis(startDate, endDate);
  if (!Number.isFinite(totalDays) || totalDays < 0) {
    return null;
  }

  const blockedDays =
    getDiasBloqueio(row, {
      periodStart: startDate,
      periodEnd: endDate,
    }) || 0;

  return Math.max(0, totalDays - blockedDays);
}

export function getLeadTimeMarcenariaProducao(
  row,
  endValue = row?.[F.MARCENARIA_TERMINO]
) {
  return getLeadTimeProducao(row, row?.[F.MARCENARIA_INICIO], endValue);
}

export function isMarcenariaBlocked(row) {
  return Boolean(toDate(row?.[F.INICIO_DO_BLOQUEIO]) && !toDate(row?.[F.FINAL_DO_BLOQUEIO]));
}

// D-032: tenta inferir motivo do bloqueio a partir do texto livre da
// coluna OBSERVACOES_GERAIS. Categorias: Insumo, Aprovacao, Cliente,
// Capacidade, Outro. Sem motivo categorizado, retorna a observacao
// bruta como dica.
export function inferirMotivoBloqueio(row) {
  if (!isMarcenariaBlocked(row)) return null;
  const obs = row?.[F.OBSERVACOES_GERAIS];
  if (!obs) return { categoria: "Sem motivo", detalhe: null };
  const texto = String(obs).toLowerCase();
  const padroes = [
    { categoria: "Insumo", regex: /(falta|aguard).*(material|insumo|mdf|ferrag|tinta|verniz|laca)/ },
    { categoria: "Insumo", regex: /(material|insumo).*(atras|nao chegou|pendente)/ },
    { categoria: "Aprovação", regex: /(aguard|pendente).*(aprov|cliente|projeto|executiv)/ },
    { categoria: "Aprovação", regex: /(reprovad|alteracao\s+do\s+projeto)/ },
    { categoria: "Cliente", regex: /(cliente).*(decid|definir|escolher|confirmar)/ },
    { categoria: "Capacidade", regex: /(sem\s+capacidade|sem\s+marceneiro|sem\s+pintura|fila)/ },
  ];
  for (const padrao of padroes) {
    if (padrao.regex.test(texto)) {
      return { categoria: padrao.categoria, detalhe: String(obs).trim() };
    }
  }
  return { categoria: "Outro", detalhe: String(obs).trim() };
}

// C-018: bloqueio detectado em qualquer ambiente, independentemente de
// estar pos-maquinas ou nao. A regra "isMarcenariaReady" so define se o
// bloqueio aparece no card "Em Marcenaria"; aqui retornamos a contagem
// total para que a Home/painel pai exiba "X bloqueios reais".
export function countAllBlockedAmbientes(dados = []) {
  if (!Array.isArray(dados)) return 0;
  return dados.filter(isMarcenariaBlocked).length;
}

export function partitionBlockedAmbientes(dados = []) {
  if (!Array.isArray(dados)) {
    return { emMarcenaria: [], outrasEtapas: [] };
  }
  const emMarcenaria = [];
  const outrasEtapas = [];
  for (const row of dados) {
    if (!isMarcenariaBlocked(row)) continue;
    if (isMarcenariaReady(row)) emMarcenaria.push(row);
    else outrasEtapas.push(row);
  }
  return { emMarcenaria, outrasEtapas };
}
